import React, { useState, useRef, useEffect } from 'react';
import { ChatMessage, WeatherData, Alert, NewsItem, LatLng } from '../types';
import { generateSafetyAdvice } from '../services/aiService';
import { supabase } from '../services/supabase';
import { getCurrentPosition, getCyberLocationName } from '../services/locationService';
import { getWeather } from '../services/weatherService';
import { fetchRealHazards } from '../services/disasterService';
import { motion, AnimatePresence } from 'framer-motion';
import {
    BrainCircuit, Send, Cpu, Activity, Shield,
    Globe, Database, Terminal
} from 'lucide-react';

interface IntelCortexProps {
    initialContext?: {
        location: LatLng;
        weather: WeatherData;
        hazards: Alert[];
        news: NewsItem[];
    } | null;
    onBack: () => void;
}

const IntelCortex: React.FC<IntelCortexProps> = ({ initialContext, onBack }) => {
    // ... (state remains same)
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [userId, setUserId] = useState<string>('');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [loadingHistory, setLoadingHistory] = useState(true);

    // Live Context State for Visualization
    const [liveContext, setLiveContext] = useState<{
        locName: string;
        temp: number;
        conditions: string;
        hazardCount: number;
    }>({ locName: 'Initializing...', temp: 0, conditions: '--', hazardCount: 0 });

    const [showContextPanel, setShowContextPanel] = useState(false);

    // ... (useEffect init remains same)
    useEffect(() => {
        const init = async () => {
            // ... (load history)
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                setUserId(user.id);
                const { data } = await supabase.from('ai_messages').select('*').eq('user_id', user.id).order('created_at', { ascending: true }).limit(50);
                if (data) setMessages(data.map(m => ({ id: m.id, user_id: m.role === 'user' ? 'user' : 'system', role: m.role as 'user' | 'model', text: m.content, timestamp: m.created_at })));
            }
            setLoadingHistory(false);
            // ... (context load)
            const ctx = await gatherContext();
            if (ctx) setLiveContext({ locName: typeof ctx.location === 'string' ? ctx.location : 'Unknown', temp: ctx.weather?.temp || 0, conditions: ctx.weather?.condition || 'Unknown', hazardCount: ctx.hazards?.length || 0 });
        };
        init();
    }, []);

    // ... (rest of logic remains same)
    // Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    // --- 2. CONTEXT GATHERING ---
    const gatherContext = async () => {
        if (initialContext) {
            const locName = await getCyberLocationName(initialContext.location.lat, initialContext.location.lng, 'en');
            return {
                location: locName,
                weather: initialContext.weather,
                hazards: initialContext.hazards,
                squadStatus: "All systems nominal"
            };
        }
        try {
            const pos = await getCurrentPosition();
            const [weather, hazards, locName] = await Promise.all([
                getWeather(pos, 'en').catch(() => null),
                fetchRealHazards(pos).catch(() => []),
                getCyberLocationName(pos.lat, pos.lng, 'en').catch(() => "Unknown Sector")
            ]);
            return {
                location: locName,
                weather: weather,
                hazards: hazards,
                squadStatus: "All systems nominal"
            };
        } catch (e) { return {}; }
    };

    // --- 3. SEND MESSAGE LOGIC ---
    const handleSend = async (textOverride?: string) => {
        const textToSend = textOverride || input;
        if (!textToSend.trim()) return;

        setInput('');

        // Optimistic UI
        const tempUserMsg: ChatMessage = {
            id: `temp-${Date.now()}`,
            user_id: 'user',
            role: 'user',
            text: textToSend,
            timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, tempUserMsg]);
        setIsTyping(true);

        try {
            if (userId) {
                await supabase.from('ai_messages').insert({ user_id: userId, role: 'user', content: textToSend });
            }

            const context = await gatherContext();
            // Update live view
            setLiveContext({
                locName: typeof context.location === 'string' ? context.location : 'Unknown',
                temp: context.weather?.temp || 0,
                conditions: context.weather?.condition || 'Unknown',
                hazardCount: context.hazards?.length || 0
            });

            const historyForAi = messages.slice(-10).map(m => ({
                role: m.role === 'user' ? 'user' : 'assistant',
                content: m.text
            }));

            const responseText = await generateSafetyAdvice(textToSend, historyForAi, context);

            if (userId) {
                await supabase.from('ai_messages').insert({ user_id: userId, role: 'model', content: responseText });
            }

            const modelMsg: ChatMessage = {
                id: `ai-${Date.now()}`,
                user_id: 'system',
                role: 'model',
                text: responseText,
                timestamp: new Date().toISOString()
            };

            setMessages(prev => [...prev, modelMsg]);

        } catch (e) {
            const errorMsg: ChatMessage = {
                id: `err-${Date.now()}`,
                user_id: 'system',
                role: 'model',
                text: "⚠️ Neural Link Unstable. Engaging offline safety protocols.",
                timestamp: new Date().toISOString()
            };
            setMessages(prev => [...prev, errorMsg]);
        } finally {
            setIsTyping(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex flex-col bg-[#020617] font-sans h-[100dvh]">

            {/* GRID BACKGROUND */}
            <div className="absolute inset-0 pointer-events-none opacity-20"
                style={{
                    backgroundImage: 'linear-gradient(rgba(37,99,235,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.1) 1px, transparent 1px)',
                    backgroundSize: '40px 40px'
                }}
            />

            {/* HEADER: CONTEXT STREAM */}
            <div className="flex-none border-b border-indigo-500/20 bg-[#0f172a]/80 backdrop-blur-md p-3 z-20">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {/* Back Button */}
                        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                        </button>

                        <div className="relative">
                            <div className="w-10 h-10 rounded-lg bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center">
                                <BrainCircuit className="w-6 h-6 text-indigo-400" />
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-[#020617] animate-pulse"></div>
                        </div>
                        <div>
                            <h2 className="text-sm font-bold text-white tracking-widest flex items-center gap-2">
                                CORTEX PRIME
                                <span className="px-1.5 py-0.5 rounded text-[9px] bg-indigo-500 text-white font-mono">V4.0</span>
                            </h2>
                            <div className="flex items-center gap-3 text-[10px] text-gray-400 font-mono mt-0.5">
                                <span className="flex items-center gap-1">
                                    <Globe className="w-3 h-3" /> {liveContext.locName.substring(0, 15)}...
                                </span>
                                <span className="w-px h-3 bg-gray-700"></span>
                                <span className="flex items-center gap-1 text-emerald-400">
                                    <Activity className="w-3 h-3" /> ONLINE
                                </span>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={() => setShowContextPanel(!showContextPanel)}
                        className={`p-2 rounded-lg transition-colors ${showContextPanel ? 'bg-indigo-500/20 text-indigo-300' : 'hover:bg-white/5 text-gray-400'}`}
                    >
                        <Database className="w-5 h-5" />
                    </button>
                </div>

                {/* EXPANDABLE CONTEXT DATA PANEL */}
                <AnimatePresence>
                    {showContextPanel && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden mt-3 pt-3 border-t border-white/5"
                        >
                            <div className="grid grid-cols-3 gap-2">
                                <div className="bg-[#020617]/50 p-2 rounded border border-indigo-500/20">
                                    <div className="text-[9px] text-indigo-400 mb-1 font-mono uppercase">Atmosphere</div>
                                    <div className="text-sm font-bold text-white">{liveContext.temp}°C</div>
                                    <div className="text-[10px] text-gray-500">{liveContext.conditions}</div>
                                </div>
                                <div className="bg-[#020617]/50 p-2 rounded border border-red-500/20">
                                    <div className="text-[9px] text-red-400 mb-1 font-mono uppercase">Threats</div>
                                    <div className="text-sm font-bold text-white">{liveContext.hazardCount}</div>
                                    <div className="text-[10px] text-gray-500">Active Alerts</div>
                                </div>
                                <div className="bg-[#020617]/50 p-2 rounded border border-emerald-500/20">
                                    <div className="text-[9px] text-emerald-400 mb-1 font-mono uppercase">System</div>
                                    <div className="text-sm font-bold text-white">NOMINAL</div>
                                    <div className="text-[10px] text-gray-500">Latency: 12ms</div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* CHAT AREA */}
            <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6 scrollbar-hide pb-32">
                {messages.length === 0 && !loadingHistory && (
                    <div className="min-h-full flex flex-col items-center justify-center opacity-80 py-10">
                        <div className="relative mb-8">
                            <div className="w-32 h-32 rounded-full border border-indigo-500/20 flex items-center justify-center animate-[spin_10s_linear_infinite]">
                                <div className="w-24 h-24 rounded-full border border-emerald-500/20 border-dashed"></div>
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <BrainCircuit className="w-12 h-12 text-indigo-400 opacity-80" />
                            </div>
                        </div>
                        <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-emerald-400 mb-2">
                            AWAITING INPUT
                        </h3>
                        <p className="text-xs text-gray-500 font-mono text-center max-w-xs mb-8">
                            Neural Link Active. Scanning local frequencies.<br />Ready for tactical analysis.
                        </p>

                        <div className="grid grid-cols-1 gap-3 w-full max-w-sm">
                            {[
                                { icon: Shield, label: "Run Risk Assessment", prompt: "Analyze current local risks." },
                                { icon: Globe, label: "Detailed Weather Report", prompt: "Give me a detailed weather briefing." },
                                { icon: Terminal, label: "Survival Protocols", prompt: "What are the survival protocols for this area?" }
                            ].map((item, i) => (
                                <button
                                    key={i}
                                    onClick={() => handleSend(item.prompt)}
                                    className="flex items-center gap-4 px-4 py-3 rounded-xl bg-[#0f172a] border border-white/5 hover:border-indigo-500/50 hover:bg-[#1e293b] transition-all group group"
                                >
                                    <div className="w-8 h-8 rounded bg-indigo-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                                        <item.icon className="w-4 h-4 text-indigo-400" />
                                    </div>
                                    <span className="text-sm text-gray-300 font-medium group-hover:text-white">{item.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map((msg, idx) => (
                    <motion.div
                        key={msg.id || idx}
                        initial={{ opacity: 0, x: msg.role === 'user' ? 20 : -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className={`flex w-full ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                        <div className={`max-w-[85%] sm:max-w-[75%] flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>

                            {/* Role Label */}
                            <span className={`text-[9px] font-mono tracking-wider mb-1 opacity-70 ${msg.role === 'user' ? 'text-emerald-400 mr-2' : 'text-indigo-400 ml-2'
                                }`}>
                                {msg.role === 'user' ? 'COMMANDER' : 'CORTEX PRIME'}
                            </span>

                            {/* Message Bubble */}
                            <div className={`
                                relative p-4 rounded-2xl backdrop-blur-sm border shadow-lg
                                ${msg.role === 'user'
                                    ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-50 rounded-tr-sm'
                                    : 'bg-[#1e293b]/80 border-indigo-500/20 text-gray-100 rounded-tl-sm'
                                }
                            `}>
                                {/* Decorative "Data" Corners for AI */}
                                {msg.role === 'model' && (
                                    <>
                                        <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-indigo-500/50"></div>
                                        <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-indigo-500/50"></div>
                                    </>
                                )}

                                <p className="text-sm leading-relaxed whitespace-pre-wrap font-sans">
                                    {msg.text}
                                </p>
                            </div>

                            {/* Timestamp */}
                            <span className="text-[9px] text-gray-600 font-mono mt-1 px-1">
                                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        </div>
                    </motion.div>
                ))}

                {isTyping && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start w-full">
                        <div className="bg-[#1e293b]/50 border border-indigo-500/10 rounded-2xl rounded-tl-sm p-4 flex items-center gap-3">
                            <Cpu className="w-4 h-4 text-indigo-400 animate-pulse" />
                            <div className="flex gap-1">
                                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce"></span>
                                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce delay-75"></span>
                                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce delay-150"></span>
                            </div>
                            <span className="text-[10px] text-indigo-300 font-mono animate-pulse">
                                ANALYZING DATA STREAMS...
                            </span>
                        </div>
                    </motion.div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* INPUT AREA */}
            <div className="p-4 bg-[#0f172a]/90 border-t border-white/5 backdrop-blur-md pb-8">
                <div className="relative group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-emerald-500 rounded-xl opacity-20 group-focus-within:opacity-50 transition-opacity blur"></div>
                    <div className="relative flex items-center bg-[#020617] rounded-xl overflow-hidden border border-white/10 group-focus-within:border-white/20">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                            placeholder="Type command or query..."
                            className="flex-1 bg-transparent border-none text-white text-sm px-4 py-4 focus:ring-0 placeholder-gray-600 font-sans"
                            autoComplete="off"
                        />
                        <button
                            onClick={() => handleSend()}
                            disabled={!input.trim() || isTyping}
                            className="p-3 mr-1 rounded-lg text-indigo-400 hover:text-white hover:bg-indigo-600 transition-all disabled:opacity-30 disabled:hover:bg-transparent"
                        >
                            <Send className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                <div className="text-[9px] text-center text-gray-600 mt-2 font-mono">
                    SECURE CONNECTION • ENCRYPTED • V4.0.2
                </div>
            </div>

        </div>
    );
};

export default IntelCortex;
